import { APP_HOME_PATH } from '@/routing/appHome';
import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { AlertCircle, Fingerprint, Globe, Info, KeyRound } from 'lucide-react';
import { useStores } from '@/hooks/useStores';
import { AUTH_WELCOME_CARD_CLASS, AuthFrame } from '@/components/AuthFrame';
import { Button } from '@/components/Button';
import { busyControlProps } from '@/utils/busyControl';
import { Input } from '@/components/Input';
import {
  oauthRefusalMessageIn,
  providerButtonsFor,
  signInIsForApp,
  useOAuthRefusalWords,
} from './oauthSignIn';
import { sessionEndMessage } from '@/services/sessionEnd';
import { UnverifiedSignInNotice } from './UnverifiedSignInNotice';
import { SetupRequiredNotice } from './SetupRequiredNotice';
import { usePasskeySignInLabel } from './passkeyLabel';
import { passkeyUsedHere } from './passkeyUsedHere';
import { unreachableAddressDetail } from '@/features/onboarding/signInFailure';
import { SIGN_UP_MODES, signUpModeOf } from '@featit/shared/app-setup';
import { legalLinks } from '@featit/shared/legal';
import { signUpClosedCopy } from '@featit/shared/sign-up-closed';
import { useAppSetup } from '@/features/app-setup/appSetup';
import {
  useAutoFocusFirstField,
  signedOutLinkClasses,
  signedOutQuietLinkClasses,
} from './signedOutChrome';
import { OAuthContinueInBrowserNotice } from './OAuthContinueInBrowserNotice';
import { ContinueInBrowserNotice } from '@/desktop/ContinueInBrowserNotice';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { FixedAddressField } from './FixedAddressField';
import { useInvitedAddress } from './invitedAddress';
import { BackToFirstScreen } from '@/features/onboarding/BackToFirstScreen';
import { LegalLink } from '@/features/legal/LegalLink';
import { LegalLinks } from '@/features/legal/LegalLinks';
import { Waiting } from '@/components/Waiting';
import { useT } from '@/i18n/LocaleProvider';
import { forgetDataRemovedNotice, signOutNoticeWaiting } from '@/privacy/signOutDataNotice';
import { registerEnglishArea } from '@/i18n/catalogue';
import { signOutChoice } from '@featit/shared/i18n/messages/en/signOutChoice';

registerEnglishArea('signOutChoice', signOutChoice);

/**
 * Where a person signs in — and, since F-0161, where a provider round trip lands.
 *
 * ⚠️ The provider callback comes back to `/login?oauthCode=…` rather than to a route of its own.
 * That is a deliberate choice and not a shortcut: this screen is already public, it is where
 * somebody expects to be while signing in, and a refusal has somewhere sensible to be shown. A
 * dedicated `/auth/callback` route would need a fourth public route whose only content is a
 * spinner and an error box this page already has.
 */
export const LoginPage = observer(function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { authStore } = useStores();
  const location = useLocation();
  // Arriving from sign-up's "check your email" carries the address, so it need not be typed twice.
  const [email, setEmail] = useState(() => {
    const carried = (location.state as { email?: unknown } | null)?.email;
    return typeof carried === 'string' ? carried : '';
  });
  const [password, setPassword] = useState('');
  /*
   * I-2181, RL-5d: from an invitation link sent to one address, the sign-in is that address's — shown
   * masked, never typed or changed, and no passkey or provider that could pick another account.
   */
  const invited = useInvitedAddress(params.get('invited') === '1');
  const fixedTo = invited.masked;

  // ADR-133: with nowhere named, a sign-in goes to the home under `/-/`, never through `/`.
  const redirectParam = params.get('redirect') ?? APP_HOME_PATH;
  const safeRedirect =
    redirectParam.startsWith('/') && !redirectParam.startsWith('//')
      ? redirectParam
      : APP_HOME_PATH;
  /*
   * Why this reader is here, when they did not choose to be (I-0464).
   *
   * A session that ended used to drop somebody at a sign-in form with no explanation — the same
   * screen as a deliberate sign-out, and indistinguishable from "it broke". The code comes from
   * `loginRedirectTarget`, and the sentence from `sessionEnd.ts`; nothing in the URL is rendered.
   */
  const ended = sessionEndMessage(params.get('sessionEnded'));
  // I-2102: a sign-out that removed the data without being able to check says so here, once.
  // I-2229: one the person chose, to continue with an invitation, says that instead.
  const [signOutNotice] = useState(signOutNoticeWaiting);
  useEffect(() => {
    if (signOutNotice) forgetDataRemovedNotice();
  }, [signOutNotice]);
  const oauthCode = params.get('oauthCode');
  const providerButtons = providerButtonsFor(authStore.oauthProviders, {
    forApp: signInIsForApp(params.get('redirect')),
  });
  const { label: passkeyLabel, namesSensor: passkeyNamesSensor } = usePasskeySignInLabel();
  const autoFocusEmail = useAutoFocusFirstField();
  // T-0488, AC-W19a: what the way to a new account says depends on how this deployment takes people.
  const setup = useAppSetup();
  const signUp = setup.phase === 'ready' ? signUpModeOf(setup.identity) : null;
  const closedCopy = signUpClosedCopy(
    setup.phase === 'ready' ? (setup.identity.earlyAccessUrl ?? null) : null,
  );
  // T-0615, AC-W19c: a provider sign-up refused on a closed deployment says what the screens say.
  // F-0621: the refusal's words load only when the address carries one, never with the entry.
  const refusalWords = useOAuthRefusalWords(params.get('oauthError'));
  const refusal = !refusalWords
    ? null
    : oauthRefusalMessageIn(
        t,
        params.get('oauthError'),
        signUp === SIGN_UP_MODES.CLOSED ? closedCopy : null,
      );

  useEffect(() => {
    void authStore.loadOAuthProviders();
    // F-0160 AC-64 (I-0638): on a deployment with no administrator, point at setup.
    void authStore.loadSetupStatus();
  }, [authStore]);

  /*
   * F-0160 AC-73 (I-0638): somebody opening a brand-new deployment while its browser door is open is
   * taken straight to it — they have no account to sign in with, and nobody does. Not when they
   * came back from setup on purpose, and not in the middle of a provider sign-in.
   */
  const cameFromSetup = (location.state as { fromSetup?: unknown } | null)?.fromSetup === true;
  const browserSetupOpen = authStore.setupRequired === true && authStore.firstRun?.open === true;
  useEffect(() => {
    if (browserSetupOpen && !cameFromSetup && !params.get('oauthCode')) {
      navigate('/setup', { replace: true });
    }
  }, [browserSetupOpen, cameFromSetup, navigate, params]);

  /*
   * Finish a provider sign-in, exactly once.
   *
   * ⛔ The code is single-use on the server, so a second attempt with the same one fails — and in
   * React 19's development mode every effect runs twice on mount. The guard is a ref rather than
   * state because it has to be set synchronously, before the await; a `useState` flag would still
   * be false when the second invocation read it.
   *
   * The parameter is also stripped from the URL immediately, so the code does not survive into
   * browser history or into the `Referer` of whatever this page loads next.
   *
   * The ref holds the code it redeemed, not a flag (ADR-160 AC-27): in the desktop app a return is
   * a client-side navigation and this page stays mounted, so after a first attempt fails — a link
   * from elsewhere, an expired code — the person's next sign-in arrives here as a new code.
   */
  const redeemed = useRef<string | null>(null);
  useEffect(() => {
    if (!oauthCode || redeemed.current === oauthCode) return;
    redeemed.current = oauthCode;
    // I-1437, ADR-160 AC-31: a desktop return names the sign-in its code belongs to.
    const desktopChallenge = params.get('desktopChallenge');
    const next = new URLSearchParams(params);
    next.delete('oauthCode');
    next.delete('desktopChallenge');
    setParams(next, { replace: true });
    void authStore.completeOAuthSignIn(oauthCode, desktopChallenge).then((ok) => {
      if (ok) navigate(safeRedirect, { replace: true });
    });
  }, [authStore, navigate, oauthCode, params, safeRedirect, setParams]);

  // T-1144: Try again repeats what was pressed, once per press; nothing retries by itself.
  const lastAttempt = useRef<'password' | 'passkey'>('password');

  async function signInWithPassword() {
    lastAttempt.current = 'password';
    if (fixedTo) await authStore.login('', password, invited.invitation);
    else await authStore.login(email, password);
    if (authStore.isAuthenticated) navigate(safeRedirect);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await signInWithPassword();
  }

  async function handlePasskeySignIn() {
    lastAttempt.current = 'passkey';
    await authStore.loginWithPasskey(email);
    if (authStore.isAuthenticated) navigate(safeRedirect);
  }

  const finishingProviderSignIn = !!oauthCode && !authStore.error;
  const unreachableDetail = authStore.signInUnreachable ? unreachableAddressDetail() : null;
  const passkeyFirst = !fixedTo && passkeyUsedHere();
  const passkeyButton = (variant: 'primary' | 'secondary') => (
    <Button
      type="button"
      variant={variant}
      size="lg"
      loading={authStore.passkeyLoading}
      className="w-full"
      onClick={handlePasskeySignIn}
    >
      {passkeyNamesSensor ? (
        <Fingerprint className="h-4 w-4" aria-hidden="true" />
      ) : (
        <KeyRound className="h-4 w-4" aria-hidden="true" />
      )}
      {passkeyLabel}
    </Button>
  );
  const orLine = (
    <div className="my-4 flex items-center gap-3">
      <span className="h-px flex-1 bg-gray-100 dark:bg-[#2a2a2a]" />
      <span className="text-[12px] text-gray-400">{t('auth.common.or')}</span>
      <span className="h-px flex-1 bg-gray-100 dark:bg-[#2a2a2a]" />
    </div>
  );

  return (
    <AuthFrame
      before={<BackToFirstScreen />}
      title={t('auth.signIn.title')}
      subtitle={t('auth.signIn.subtitle')}
      welcoming
    >
      <div className={AUTH_WELCOME_CARD_CLASS}>
        {/*
         * F-0160 AC-64: above the form rather than instead of it. A deployment that lost its
         * administrators still has accounts that can sign in; a new one has none, and the notice
         * is the way forward.
         */}
        {authStore.setupRequired === true && (
          <div className="mb-4">
            <SetupRequiredNotice />
          </div>
        )}

        {/*
         * Blue rather than the refusal's amber: nothing was refused and nobody did anything wrong
         * — a session ran out, or was ended elsewhere. The amber box below is for a sign-in the
         * deployment would not complete, and the two should not look like the same event.
         */}
        {signOutNotice === 'switched' || signOutNotice === 'switched-removed' ? (
          <div
            role="status"
            data-testid="signed-out-to-switch"
            className="mb-4 flex items-start gap-2.5 rounded-md border border-gray-200 bg-gray-100 px-3 py-2.5 text-[13px] text-gray-800 dark:border-white/10 dark:bg-white/10 dark:text-gray-100"
          >
            <Info className="mt-px h-4 w-4 shrink-0" />
            <span>
              {t(
                signOutNotice === 'switched'
                  ? 'auth.invited.switched'
                  : 'auth.invited.switchedRemoved',
              )}
            </span>
          </div>
        ) : null}

        {signOutNotice === 'unchecked' && (
          <div
            role="status"
            data-testid="data-removed-unchecked"
            className="mb-4 flex items-start gap-2.5 rounded-md border border-gray-200 bg-gray-100 px-3 py-2.5 text-[13px] text-gray-800 dark:border-white/10 dark:bg-white/10 dark:text-gray-100"
          >
            <Info className="mt-px h-4 w-4 shrink-0" />
            <span>{t('signOutChoice.removedUnchecked')}</span>
          </div>
        )}

        {ended && (
          <div
            role="status"
            className="mb-4 flex items-start gap-2.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-[13px] text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-100"
          >
            <Info className="mt-px h-4 w-4 shrink-0" />
            <span>
              <span className="font-medium">{t(ended.i18n.title)}</span>
              <span className="mt-0.5 block">{t(ended.i18n.action)}</span>
            </span>
          </div>
        )}

        {/*
         * The refusal comes back as a code in the URL and is turned into a sentence here — see
         * `oauthSignIn.tsx`. `account-exists` in particular gets a next step rather than a "no",
         * because the person reading it is usually the legitimate owner trying a provider for the
         * first time.
         */}
        {refusal && (
          <div
            role="alert"
            className="mb-4 flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100"
          >
            <AlertCircle className="mt-px h-4 w-4 shrink-0" />
            <span>
              <span className="font-medium">{refusal.title}</span>
              {refusal.action && <span className="mt-0.5 block">{refusal.action}</span>}
              {refusal.link && (
                <span className="mt-1 block">
                  <LegalLink href={refusal.link.href} className="font-medium evx-banner-action">
                    {refusal.link.label}
                  </LegalLink>
                </span>
              )}
            </span>
          </div>
        )}

        {/*
         * F-0160 AC-25: the password was right and the address was never confirmed. Not an error
         * box — nothing the person typed is wrong — but the next step, with a new link one click
         * away (AC-25b), using the email and password still in the form.
         */}
        {authStore.unverifiedSignInEmail && (
          <UnverifiedSignInNotice email={email} password={password} />
        )}

        {authStore.signInUnreachable ? (
          <div
            role="alert"
            data-testid="sign-in-unreachable"
            className="mb-4 flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100"
          >
            <AlertCircle className="mt-px h-4 w-4 shrink-0" />
            <span className="min-w-0">
              <span className="font-medium">{t('auth.signInFailure.cannotReachNow')}</span>
              <span className="mt-0.5 block">{t('auth.signInFailure.neverChecked')}</span>
              <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <button
                  type="button"
                  className="font-medium evx-banner-action"
                  onClick={() =>
                    void (lastAttempt.current === 'passkey'
                      ? handlePasskeySignIn()
                      : signInWithPassword())
                  }
                >
                  {t('navigation.tryAgain')}
                </button>
                {unreachableDetail ? (
                  <details className="min-w-0">
                    <summary className="cursor-pointer">{t('auth.signInFailure.details')}</summary>
                    <span className="mt-1 block [overflow-wrap:anywhere]">{unreachableDetail}</span>
                  </details>
                ) : null}
              </span>
            </span>
          </div>
        ) : authStore.error ? (
          <div
            role="alert"
            className="mb-4 flex items-start gap-2.5 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-100"
          >
            <AlertCircle className="mt-px h-4 w-4 shrink-0" />
            <span>{authStore.error}</span>
          </div>
        ) : null}

        {/*
         * Coming back from a provider is a state, not an absence of one. Without this the screen
         * shows an empty sign-in form for the second it takes to exchange the code, which reads
         * as "it did not work" to somebody who has just successfully signed in elsewhere.
         */}
        {finishingProviderSignIn || invited.checking ? (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center justify-center gap-2.5 py-8 text-[13px] text-gray-500 dark:text-gray-400"
          >
            <Waiting size="inline" />
            {invited.checking ? t('auth.invited.checking') : t('auth.signIn.finishing')}
          </div>
        ) : (
          <>
            {passkeyFirst ? (
              <>
                {passkeyButton('primary')}
                {orLine}
              </>
            ) : null}
            <form onSubmit={handleSubmit} className="space-y-4">
              {fixedTo ? (
                <FixedAddressField address={fixedTo} hint={t('auth.invited.signInHint')} />
              ) : (
                <Input
                  id="email"
                  name="email"
                  label={t('auth.common.email')}
                  type="email"
                  placeholder={t('auth.common.emailPlaceholder')}
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus={autoFocusEmail}
                  touchTarget
                  required
                  quietRequired
                />
              )}
              <div>
                <Input
                  id="password"
                  name="password"
                  label={t('auth.common.password')}
                  type="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus={!!fixedTo && autoFocusEmail}
                  touchTarget
                  required
                  quietRequired
                />
                {/* On a phone the link's 44 px box supplies the spacing the margin gives on desktop. */}
                <div className="-mb-2 text-right sm:mb-0 sm:mt-1.5">
                  <Link to="/forgot-password" className={signedOutQuietLinkClasses}>
                    {t('auth.signIn.forgotPassword')}
                  </Link>
                </div>
              </div>
              <Button
                type="submit"
                variant={passkeyFirst ? 'secondary' : 'primary'}
                size="lg"
                loading={authStore.loading}
                className="w-full"
              >
                {t('auth.common.signIn')}
              </Button>
            </form>

            {!fixedTo && (
              <>
                {!passkeyFirst || providerButtons.length > 0 ? orLine : null}

                <div className="space-y-2">
                  {passkeyFirst ? null : passkeyButton('secondary')}

                  {/*
                   * Only the providers this deployment configured. A deployment with none shows
                   * exactly what it showed before this feature existed — no dead buttons, and nobody
                   * reading a "not configured" error that was never theirs to fix.
                   */}
                  {/* F-0819: already signed in in the browser? One press there signs this app in. */}
                  {isDesktopRuntime() ? (
                    authStore.browserSignInOpen && !oauthCode ? (
                      <ContinueInBrowserNotice
                        testId="browser-sign-in-open"
                        title={t('banners.oauth.continueInBrowser')}
                        detail={t('auth.signIn.browserDetail')}
                        dismissLabel={t('banners.oauth.useAnotherWay')}
                        onDismiss={() => authStore.dismissBrowserSignIn()}
                      />
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        size="lg"
                        className="w-full"
                        onClick={() => void authStore.beginBrowserSignIn()}
                      >
                        <Globe className="h-4 w-4" aria-hidden="true" />
                        {t('auth.signIn.withBrowser')}
                      </Button>
                    )
                  ) : null}
                  <OAuthContinueInBrowserNotice purpose="sign-in" />
                  {providerButtons.map((button) => (
                    <Button
                      key={button.provider}
                      type="button"
                      variant="secondary"
                      size="lg"
                      className="w-full"
                      loading={authStore.oauthLoading === button.provider}
                      {...busyControlProps(
                        authStore.oauthLoading !== null &&
                          authStore.oauthLoading !== button.provider,
                      )}
                      onClick={() => void authStore.beginOAuthSignIn(button.provider, safeRedirect)}
                    >
                      {button.icon}
                      {t('auth.common.continueWith', { provider: button.label })}
                    </Button>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {fixedTo ? (
        <p className="mt-5 text-center text-[13px] text-gray-500 dark:text-gray-400">
          {t('auth.invited.noAccount')}{' '}
          <Link
            to={`/register?invited=1&redirect=${encodeURIComponent(safeRedirect)}`}
            className={signedOutLinkClasses}
          >
            {t('auth.signIn.createOne')}
          </Link>
        </p>
      ) : setup.phase === 'loading' ? (
        // Holds the line's height — the link's own, a 44 px target on a phone — so the card does not
        // move when the answer arrives. No words: nothing here may be read or clicked yet.
        <p aria-hidden="true" className="invisible mt-5 text-center text-[13px]">
          <span className={signedOutLinkClasses}>&nbsp;</span>
        </p>
      ) : signUp === SIGN_UP_MODES.INVITATION ? (
        <p
          data-testid="sign-in-invitation-only"
          className="mt-5 text-center text-[13px] text-gray-500 dark:text-gray-400"
        >
          {t('auth.signIn.invitationOnly')}
          <span className="block">{t('auth.signIn.askToInvite')}</span>
        </p>
      ) : signUp === SIGN_UP_MODES.CLOSED ? (
        <p
          data-testid="sign-in-sign-up-closed"
          className="mt-5 text-center text-[13px] text-gray-500 dark:text-gray-400"
        >
          {/* T-0615, AC-W19c: the early-access form where the deployment names one. */}
          {closedCopy.signInNote}
          {closedCopy.earlyAccessUrl ? (
            <span className="block">
              <LegalLink href={closedCopy.earlyAccessUrl} className={signedOutLinkClasses}>
                {closedCopy.actionLabel}
              </LegalLink>
            </span>
          ) : (
            <span className="block">{closedCopy.askInstead}</span>
          )}
        </p>
      ) : (
        <p className="mt-5 text-center text-[13px] text-gray-500 dark:text-gray-400">
          {t('auth.signIn.noAccount')}{' '}
          <Link to="/register" className={signedOutLinkClasses}>
            {t('auth.signIn.createOne')}
          </Link>
        </p>
      )}
      {/* F-0554, AC-TA9: Imprint, Privacy and Terms, reachable before an account exists. */}
      <LegalLinks
        links={setup.phase === 'ready' ? legalLinks(setup.identity.websiteOrigin) : null}
      />
    </AuthFrame>
  );
});
